// ─────────────────────────────────────────────────────────────────────────────
// NCRSection — Non-Conformance Register.
// Construction Engineering Cockpit aesthetic. Token-driven, primitive-driven.
// Logic preserved from original; presentation rebuilt on ./ui.jsx primitives.
//
// IMPORTANT: keeps the `emptyNcrForm` named export — App.js imports it directly.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo } from "react";
import { tokens } from "./theme";
import {
  Card, BlueprintGrid, Reveal, Eyebrow,
  Input, Select, Textarea, FormRow, FormGroupDivider,
  Button, Badge,
  Stat,
  Table, Th, Td,
  Empty,
} from "./ui";

const t = tokens;

// ─── Empty form shape — preserved API, App.js imports this ──────────────────
export const emptyNcrForm = {
  projectId: "",
  ncrNo: "",
  issueDate: "",
  contractor: "",
  description: "",
  category: "Workmanship",
  status: "Open",
  severity: "Medium",
  responsibleParty: "Contractor",
  location: "",
  specificationReference: "",
  drawingReference: "",
  instructionReference: "",
  rfiReference: "",
  clauseReference: "",
  requiredCloseOutDate: "",
  dateResolved: "",
  correctiveAction: "",
  rootCause: "",
  costImpact: "Potential",
  timeImpact: "Potential",
  linkedVariationId: "",
  engineerRemarks: "",
};

function getNextNcrNumber(currentProjectId, ncrs) {
  const projectNcrs = (ncrs || []).filter((item) => item.projectId === currentProjectId);
  return `NCR-${String(projectNcrs.length + 1).padStart(3, "0")}`;
}

// ─── Status / severity / impact variant mapping ─────────────────────────────
function statusVariant(status) {
  return status === "Resolved" ? "success" : "danger";
}
function severityVariant(s) {
  switch (s) {
    case "Critical": return "danger";
    case "High":     return "warning";
    case "Medium":   return "info";
    case "Low":      return "default";
    default:         return "default";
  }
}
function impactVariant(v) {
  switch (v) {
    case "Confirmed": return "danger";
    case "Potential": return "warning";
    case "None":      return "success";
    default:          return "default";
  }
}

// ─── Page-specific icons ─────────────────────────────────────────────────────
const I = {
  add:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  save:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  view:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  link:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  alert:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function NCRSection({
  ncrForm,
  setNcrForm,
  handleAddOrUpdateNcr,
  ncrs,
  currentProjectId,
  editingNcrId,
  handleEditNcr,
  handleDeleteNcr,
  clearNcrForm,
  handleCreateVariationFromNcr,
  handleViewItem,
  variations = [],
}) {
  const projectNcrs = useMemo(
    () => (ncrs || []).filter((item) => item.projectId === currentProjectId),
    [ncrs, currentProjectId]
  );

  const counts = useMemo(() => ({
    total:    projectNcrs.length,
    open:     projectNcrs.filter((i) => i.status === "Open").length,
    resolved: projectNcrs.filter((i) => i.status === "Resolved").length,
    linkedVo: projectNcrs.filter((i) => String(i.linkedVariationId || "").trim()).length,
    critical: projectNcrs.filter((i) => i.severity === "Critical" && i.status === "Open").length,
  }), [projectNcrs]);

  const nextNcrNo = getNextNcrNumber(currentProjectId, ncrs || []);

  const linkedVariationLabel = (variationId) => {
    const match = (variations || []).find((item) => item.id === variationId);
    if (!match) return variationId || "";
    return match.variationNo || match.id;
  };

  const updateForm = (field, value) =>
    setNcrForm({ ...ncrForm, [field]: value, projectId: currentProjectId });

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{ position: "relative", display: "grid", gap: 18, fontFamily: t.font.body }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0}>
          <Card padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
            }} />
            <div style={{ padding: "24px 26px", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Eyebrow>Quality Control</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                  <span style={{
                    fontFamily: t.font.mono, fontSize: 11, fontWeight: 600,
                    color: t.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                  {counts.critical > 0 && (
                    <>
                      <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.12em", textTransform: "uppercase",
                        color: t.color.danger,
                      }}>
                        {I.alert} {counts.critical} Critical Open
                      </span>
                    </>
                  )}
                </div>
                <h1 style={{
                  margin: 0, fontFamily: t.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: t.color.ink,
                }}>Non-Conformance Register</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5, maxWidth: 720 }}>
                  Capture contractor non-conformances, track close-out, record specification
                  or drawing references, and escalate to a variation order where commercial
                  consequences arise.
                </p>
              </div>
              {editingNcrId && (
                <Button variant="subtle" onClick={clearNcrForm} icon={I.cancel}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </Card>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 10,
          }}>
            <Stat label="Total NCRs"    rawValue={counts.total}    format={(v) => Math.round(v)} value={counts.total}    accent={t.color.primary} delay={80}  helper="Captured on this project" />
            <Stat label="Open"          rawValue={counts.open}     format={(v) => Math.round(v)} value={counts.open}     accent={t.color.danger}  delay={140} helper="Awaiting close-out" />
            <Stat label="Resolved"      rawValue={counts.resolved} format={(v) => Math.round(v)} value={counts.resolved} accent={t.color.success} delay={200} helper="Closed-out by contractor" />
            <Stat label="Linked to VOs" rawValue={counts.linkedVo} format={(v) => Math.round(v)} value={counts.linkedVo} accent={t.color.accent}  delay={260} helper="Escalated to variations" />
            <Stat label="Critical Open" rawValue={counts.critical} format={(v) => Math.round(v)} value={counts.critical} accent={t.color.warning} delay={320} helper="Severity = Critical" />
          </div>
        </Reveal>

        {/* ───── FORM CARD ───── */}
        <Reveal delay={220}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow>{editingNcrId ? "Editing record" : "New record"}</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>{editingNcrId ? "Edit NCR" : "Create NCR"}</h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5 }}>
                Record the non-conformance, assign close-out responsibility, and note whether
                it may lead to additional cost or time implications.
              </p>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              <FormGroupDivider>Identity</FormGroupDivider>

              <FormRow label="NCR Number" helper="Automatically suggested. Adjust manually if your project numbering differs.">
                <Input mono value={ncrForm.ncrNo || nextNcrNo}
                  onChange={(e) => updateForm("ncrNo", e.target.value)} />
              </FormRow>

              <FormRow label="Issue Date" helper="Date on which the non-conformance was formally raised.">
                <Input type="date" value={ncrForm.issueDate || ""}
                  onChange={(e) => updateForm("issueDate", e.target.value)} />
              </FormRow>

              <FormRow label="Contractor" helper="Main contractor or responsible subcontractor.">
                <Input value={ncrForm.contractor || ""}
                  onChange={(e) => updateForm("contractor", e.target.value)} />
              </FormRow>

              <FormRow label="Responsible Party" helper="Normally Contractor, but can also be Supplier, Subcontractor or Design Team.">
                <Input value={ncrForm.responsibleParty || ""}
                  onChange={(e) => updateForm("responsibleParty", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Classification</FormGroupDivider>

              <FormRow label="Description" helper="Describe the non-conformance clearly enough for follow-up and possible contractual action.">
                <Textarea rows={3} value={ncrForm.description || ""}
                  onChange={(e) => updateForm("description", e.target.value)} />
              </FormRow>

              <FormRow label="Category" helper="Classify the type of non-conformance for reporting and trend tracking.">
                <Select value={ncrForm.category || "Workmanship"}
                  onChange={(e) => updateForm("category", e.target.value)}>
                  <option value="Workmanship">Workmanship</option>
                  <option value="Material">Material</option>
                  <option value="Process">Process</option>
                  <option value="Testing">Testing</option>
                  <option value="Documentation">Documentation</option>
                  <option value="Health & Safety">Health &amp; Safety</option>
                  <option value="Environmental">Environmental</option>
                </Select>
              </FormRow>

              <FormRow label="Status" helper="Use Open until the contractor has corrected and closed out the item.">
                <Select value={ncrForm.status || "Open"}
                  onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="Open">Open</option>
                  <option value="Resolved">Resolved</option>
                </Select>
              </FormRow>

              <FormRow label="Severity" helper="Useful for dashboards, prioritisation and contractor close-out meetings.">
                <Select value={ncrForm.severity || "Medium"}
                  onChange={(e) => updateForm("severity", e.target.value)}>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </Select>
              </FormRow>

              <FormGroupDivider>References</FormGroupDivider>

              <FormRow label="Location" helper="Physical location, structure, chainage, area or plant item where the NCR applies.">
                <Input value={ncrForm.location || ""}
                  onChange={(e) => updateForm("location", e.target.value)} />
              </FormRow>

              <FormRow label="Specification Reference" helper="Relevant specification clause or section reference.">
                <Input mono value={ncrForm.specificationReference || ""}
                  onChange={(e) => updateForm("specificationReference", e.target.value)} />
              </FormRow>

              <FormRow label="Drawing Reference" helper="Relevant drawing number or revision reference.">
                <Input mono value={ncrForm.drawingReference || ""}
                  onChange={(e) => updateForm("drawingReference", e.target.value)} />
              </FormRow>

              <FormRow label="Cross-References" helper="Optional supporting references to link the NCR to other project records.">
                <div style={{ display: "grid", gap: 8 }}>
                  <Input mono placeholder="Instruction reference" value={ncrForm.instructionReference || ""}
                    onChange={(e) => updateForm("instructionReference", e.target.value)} />
                  <Input mono placeholder="RFI reference" value={ncrForm.rfiReference || ""}
                    onChange={(e) => updateForm("rfiReference", e.target.value)} />
                  <Input mono placeholder="Clause reference" value={ncrForm.clauseReference || ""}
                    onChange={(e) => updateForm("clauseReference", e.target.value)} />
                </div>
              </FormRow>

              <FormGroupDivider>Close-Out</FormGroupDivider>

              <FormRow label="Required Close-Out Date" helper="Target date by which the contractor must resolve the NCR.">
                <Input type="date" value={ncrForm.requiredCloseOutDate || ""}
                  onChange={(e) => updateForm("requiredCloseOutDate", e.target.value)} />
              </FormRow>

              <FormRow label="Date Resolved" helper="Only complete once the NCR has been satisfactorily closed out.">
                <Input type="date" value={ncrForm.dateResolved || ""}
                  onChange={(e) => updateForm("dateResolved", e.target.value)} />
              </FormRow>

              <FormRow label="Corrective Action" helper="Action required from the contractor to rectify or replace the non-conforming work.">
                <Textarea rows={3} value={ncrForm.correctiveAction || ""}
                  onChange={(e) => updateForm("correctiveAction", e.target.value)} />
              </FormRow>

              <FormRow label="Root Cause" helper="Optional field for lessons learnt and repeat-event prevention.">
                <Textarea rows={3} value={ncrForm.rootCause || ""}
                  onChange={(e) => updateForm("rootCause", e.target.value)} />
              </FormRow>

              <FormGroupDivider>Commercial &amp; Linkage</FormGroupDivider>

              <FormRow label="Cost &amp; Time Impact" helper="Flag whether the NCR may lead to commercial or programme implications.">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <Select value={ncrForm.costImpact || "Potential"}
                    onChange={(e) => updateForm("costImpact", e.target.value)}>
                    <option value="None">No Cost Impact</option>
                    <option value="Potential">Potential Cost Impact</option>
                    <option value="Confirmed">Confirmed Cost Impact</option>
                  </Select>
                  <Select value={ncrForm.timeImpact || "Potential"}
                    onChange={(e) => updateForm("timeImpact", e.target.value)}>
                    <option value="None">No Time Impact</option>
                    <option value="Potential">Potential Time Impact</option>
                    <option value="Confirmed">Confirmed Time Impact</option>
                  </Select>
                </div>
              </FormRow>

              <FormRow label="Linked Variation" helper="Populated automatically once a variation is created from the NCR.">
                <Input mono readOnly value={linkedVariationLabel(ncrForm.linkedVariationId)}
                  placeholder="No linked variation" />
              </FormRow>

              <FormRow label="Engineer Remarks" helper="Additional notes, follow-up comments or contract administration remarks.">
                <Textarea rows={3} value={ncrForm.engineerRemarks || ""}
                  onChange={(e) => updateForm("engineerRemarks", e.target.value)} />
              </FormRow>

              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                <Button variant="accent" onClick={handleAddOrUpdateNcr} icon={editingNcrId ? I.save : I.add}>
                  {editingNcrId ? "Update NCR" : "Save NCR"}
                </Button>
                {editingNcrId && (
                  <Button variant="subtle" onClick={clearNcrForm} icon={I.cancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Reveal>

        {/* ───── TABLE CARD ───── */}
        <Reveal delay={300}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 16px" }}>
              <Eyebrow>Register</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: t.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: t.color.ink,
              }}>NCR Register</h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5 }}>
                Review open and resolved NCRs, jump into edits, and escalate to a linked VO when needed.
              </p>
            </div>

            <div style={{ padding: "0 22px 22px" }}>
              {projectNcrs.length === 0 ? (
                <Empty
                  title="No NCRs captured yet"
                  message="Use the form above to capture the first non-conformance for this project."
                />
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>NCR No</Th>
                      <Th>Issue Date</Th>
                      <Th>Description</Th>
                      <Th>Status</Th>
                      <Th>Severity</Th>
                      <Th>Spec Ref</Th>
                      <Th>Drawing Ref</Th>
                      <Th>Close-Out</Th>
                      <Th>VO Link</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectNcrs.map((row) => {
                      const linkedVo = linkedVariationLabel(row.linkedVariationId);
                      const showCreateVo = !row.linkedVariationId;
                      return (
                        <tr key={row.id}>
                          <Td mono weight={700}>{row.ncrNo || "—"}</Td>
                          <Td mono>{row.issueDate || <span style={{ color: t.color.inkSubtle }}>—</span>}</Td>
                          <Td>
                            {row.description
                              ? <span style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  maxWidth: 280,
                                }}>{row.description}</span>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td><Badge variant={statusVariant(row.status)} size="sm">{row.status || "—"}</Badge></Td>
                          <Td>
                            {row.severity
                              ? <Badge variant={severityVariant(row.severity)} size="sm">{row.severity}</Badge>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td mono>{row.specificationReference || row.clauseReference || <span style={{ color: t.color.inkSubtle }}>—</span>}</Td>
                          <Td mono>{row.drawingReference || <span style={{ color: t.color.inkSubtle }}>—</span>}</Td>
                          <Td mono>{row.requiredCloseOutDate || <span style={{ color: t.color.inkSubtle }}>—</span>}</Td>
                          <Td mono>
                            {linkedVo
                              ? <span style={{ color: t.color.primary, fontWeight: 700 }}>{linkedVo}</span>
                              : <span style={{ color: t.color.inkSubtle }}>—</span>}
                          </Td>
                          <Td align="right">
                            <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <Button size="sm" variant="success" onClick={() => handleViewItem(`NCR ${row.ncrNo}`, row)} icon={I.view}>
                                View
                              </Button>
                              <Button size="sm" variant="subtle" onClick={() => handleEditNcr(row)} icon={I.edit}>
                                Edit
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => handleDeleteNcr(row.id)} icon={I.trash}>
                                Delete
                              </Button>
                              {showCreateVo && (
                                <Button size="sm" variant="accent" onClick={() => handleCreateVariationFromNcr(row)} icon={I.link}>
                                  Create VO
                                </Button>
                              )}
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </Reveal>

      </div>
    </section>
  );
}

export default memo(NCRSection);